import { Link, Navigate, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../../api/client.js';
import { keys, useSession } from '../../api/hooks.js';
import { Chapi } from '../../components/Chapi.js';
import { nextRoute } from '../../lib/two-factor.js';
import { AuthLayout } from './AuthLayout.js';

/**
 * Asks whether an email signs in through single sign-on (docs/adr/0055) and, if so, goes to the identity provider.
 * Returns false when the domain has no SSO, so the caller carries on with the email link.
 */
export async function continueWithSso(email: string): Promise<boolean> {
  const r = await api('startSso', { body: { email } });
  if (!r.sso || !r.url) return false;
  window.location.assign(r.url);
  return true;
}

/** The identity provider sent the browser back and the API set the session: open the app where it belongs. */
export function SsoDone() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api('session')
      .then((state) => {
        qc.setQueryData(keys.session, state);
        void navigate({ to: nextRoute(state), replace: true });
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'We could not finish signing you in.'));
  }, [navigate, qc]);
  return (
    <AuthLayout>
      {error ? (
        <>
          <Chapi pose="sorry" size={90} />
          <h1>That did not work</h1>
          <p role="alert">{error}</p>
          <Link className="btn btn-primary" to="/sign-in">Back to sign in</Link>
        </>
      ) : (
        <>
          <Chapi pose="working" size={90} />
          <h1 role="status">Signing you in…</h1>
        </>
      )}
    </AuthLayout>
  );
}

/** The current workspace requires single sign-on, and this device signed in another way. */
export function SsoRequired() {
  const session = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (session.isPending) {
    return (
      <AuthLayout>
        <h1 role="status">Loading…</h1>
      </AuthLayout>
    );
  }
  if (!session.data) return <Navigate to="/sign-in" />;
  const required = session.data.ssoRequired;
  if (!required) return <Navigate to={nextRoute(session.data)} />;
  const email = session.data.user.email;

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!(await continueWithSso(email))) setError('Single sign-on is not available right now. Ask your administrator.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach your identity provider. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const signOut = async () => {
    await api('signOut').catch(() => undefined);
    qc.clear();
    void navigate({ to: '/sign-in' });
  };
  return (
    <AuthLayout>
      <h1>{required.workspaceName} uses single sign-on</h1>
      <p>To open this workspace, sign in through your organisation's identity provider as {email}.</p>
      {error && <div className="error" role="alert">{error}</div>}
      <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void go()}>
        {busy ? 'Opening your identity provider…' : 'Continue with single sign-on'}
      </button>
      <button type="button" className="link" onClick={() => void signOut()}>Sign out</button>
    </AuthLayout>
  );
}
